import axios from './axiosConfig';
import { Document, DocumentType, DocumentCategory, isFolder, isFile } from '../types/document.types';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

const BASE_URL = '/clients';

export const getDocuments = async (clientId: string, propertyId?: string): Promise<DocumentType[]> => {
  try {
    const url = propertyId
      ? `${BASE_URL}/${clientId}/properties/${propertyId}/documents`
      : `${BASE_URL}/${clientId}/documents`;
    
    // The server returns data in { success: true, data: [...] } format
    const response = await axios.get<ApiResponse<DocumentType[]>>(url);
    
    // Extract the data array from the response
    const documents = response.data?.data || [];
    
    // For property documents, only show documents from the general category
    // For client documents, show all documents
    const filteredDocuments = propertyId
      ? documents.filter(doc => {
          // Only include files (not folders) that are in the GENERAL category or have no category
          return doc.type === 'file' && 
                 (!doc.category || doc.category === 'GENERAL');
        })
      : documents;
    
    return filteredDocuments.map(doc => {
      if (isFolder(doc)) {
        return doc;
      }
      return doc;
    });
  } catch (error) {
    console.error('Error fetching documents:', error);
    throw error;
  }
};

export const uploadDocument = async (
  clientId: string,
  file: File,
  category: DocumentCategory,
  propertyId?: string,
  customPath?: string
): Promise<Document> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    
    // The server expects these parameters in the URL params, not in the form data
    // Remove them from form data to avoid confusion
    if (customPath) formData.append('path', customPath);
    
    // Use the correct endpoint as defined in the server routes
    // For client documents: /clients/:clientId/documents
    // For property documents: /clients/:clientId/properties/:propertyId/documents/upload
    const url = propertyId
      ? `${BASE_URL}/${clientId}/properties/${propertyId}/documents/upload`
      : `${BASE_URL}/${clientId}/documents`;

    const response = await axios.post<ApiResponse<Document>>(
      url,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    
    if (!response.data || !response.data.data) {
      throw new Error('Invalid response format');
    }
    
    const doc = response.data.data;
    if (!isFile(doc)) {
      throw new Error('Expected file type in response');
    }
    
    return doc;
  } catch (error) {
    console.error('Document upload error:', error);
    throw error;
  }
};

export const deleteDocument = async (clientId: string, documentId: string): Promise<void> => {
  try {
    await axios.delete(`${BASE_URL}/${clientId}/documents/${documentId}`);
  } catch (error) {
    
    throw error;
  }
};

/**
 * Refresh an expired pre-signed URL for a stored document record.
 * Audit 30A: the arbitrary-key refresh-url oracle was removed — the server
 * authorises the caller against the document's parent resource.
 * @param documentId The Document record id
 * @returns A fresh pre-signed URL
 */
export const refreshUrl = async (documentId: string): Promise<string> => {
  try {
    const response = await axios.post<ApiResponse<{ url: string }>>(`/documents/${documentId}/refresh-url`);

    if (!response.data || !response.data.success || !response.data.data?.url) {
      throw new Error('Failed to refresh URL');
    }

    return response.data.data.url;
  } catch (error) {
    console.error('Error refreshing document URL:', error);
    throw error;
  }
};
